import { filterPulsesForAdminVisibility, type AdminFilterablePulse, type AdminPulseVisibility } from './admin-pulse-filter';

/**
 * Outgoing emails that delivered a Preview/Final link carry the linked share's
 * own pulse id + type in `pulses.metadata` (stamped after confirmed delivery).
 * Rows render the same direct open-link action share pulses have; by-pulse
 * lookups must use the SHARE's pulse id, never the email pulse id.
 */
export interface LinkedShareRef {
  sharePulseId: number;
  shareType: 'preview' | 'final';
}

export interface LinkedSharePulse extends AdminFilterablePulse {
  id: number;
  linkedSharePulseId?: number | null;
  linkedShareType?: 'preview' | 'final' | null;
  linkedShareActive?: boolean | null;
  sequences?: Array<{ sequenceNumber: string }>;
}

/** Only server-validated, permission-safe fields authorize a linked action. */
export function getLinkedShareFromPulse(pulse: LinkedSharePulse): LinkedShareRef | null {
  if (pulse.category !== 'email_out'
    || !Number.isSafeInteger(pulse.linkedSharePulseId)
    || (pulse.linkedSharePulseId ?? 0) <= 0
    || (pulse.linkedShareType !== 'preview' && pulse.linkedShareType !== 'final')) return null;
  return { sharePulseId: pulse.linkedSharePulseId!, shareType: pulse.linkedShareType };
}

/**
 * Display-only consolidation. Call AFTER permission/admin/sequence filtering.
 * Multiple deliveries remain separate communication entries; removing the last
 * visible delivery automatically restores its share. Never mutate cache rows.
 */
export function consolidateLinkedSharePulses<T extends LinkedSharePulse>(
  entries: readonly T[],
  visibleEmails: readonly LinkedSharePulse[] = entries,
): T[] {
  const delivered = new Set(
    visibleEmails.flatMap(pulse => {
      const ref = getLinkedShareFromPulse(pulse);
      return ref ? [ref.sharePulseId] : [];
    }),
  );
  return entries.filter(pulse =>
    !((pulse.category === 'preview' || pulse.category === 'final') && delivered.has(pulse.id)),
  );
}

export function projectDisplayedPulses<T extends LinkedSharePulse>(
  pulses: T[] | undefined | null,
  isAdmin: boolean,
  adminVisibility: AdminPulseVisibility,
  selectedSequences: readonly string[] = [],
  sequenceMap?: Record<number, Array<{ sequenceNumber: string }>>,
): T[] {
  const visible = filterPulsesForAdminVisibility(pulses, isAdmin, adminVisibility)
    .filter(pulse => selectedSequences.length === 0
      || (sequenceMap?.[pulse.id] ?? pulse.sequences ?? [])
        .some(sequence => selectedSequences.includes(sequence.sequenceNumber)));
  return consolidateLinkedSharePulses(visible);
}

/** Keep permission-neutral activity timings, but consolidate only visible mail. */
export function projectDisplayedPulseTimings<T extends LinkedSharePulse>(
  timings: readonly T[],
  visiblePulses: readonly LinkedSharePulse[],
): T[] {
  const visibleEmailIds = new Set(visiblePulses.filter(p => getLinkedShareFromPulse(p)).map(p => p.id));
  return consolidateLinkedSharePulses(timings, visiblePulses).map(pulse =>
    pulse.category === 'email_out' && !visibleEmailIds.has(pulse.id)
      ? { ...pulse, linkedSharePulseId: null, linkedShareType: null, linkedShareActive: null }
      : pulse,
  );
}

/** A derived view, never a cache write: displayed counts follow displayed rows. */
export function projectLinkedShareDisplay<T extends {
  pulses?: LinkedSharePulse[];
  pulseTimings?: LinkedSharePulse[];
  pulseCount: number;
}>(
  project: T,
  isAdmin: boolean,
  adminVisibility: AdminPulseVisibility,
  selectedSequences: readonly string[] = [],
  sequenceMap?: Record<number, Array<{ sequenceNumber: string }>>,
) {
  const pulses = projectDisplayedPulses(project.pulses, isAdmin, adminVisibility, selectedSequences, sequenceMap);
  return {
    ...project,
    pulses,
    pulseCount: pulses.length,
    pulseTimings: project.pulseTimings
      ? projectDisplayedPulseTimings(project.pulseTimings, pulses)
      : undefined,
  };
}

export function getLinkedShareFromPulseMetadata(
  category: string | null | undefined,
  metadata: Record<string, unknown> | null | undefined,
): LinkedShareRef | null {
  if (category !== 'email_out' || !metadata) return null;
  const sharePulseId = metadata.sharePulseId;
  if (typeof sharePulseId !== 'number' || !Number.isInteger(sharePulseId)) return null;
  return { sharePulseId, shareType: metadata.shareType === 'final' ? 'final' : 'preview' };
}
